/* ============================================
   Detalle de una app (/portal/apps/:slug)
   Vista rica para que el cliente entienda cómo funciona y cómo se consume
   el crédito ANTES de activarla. Reutiliza GET /api/portal/apps y filtra
   por slug (sirve tanto la vitrina como sus activaciones).
   ============================================ */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { PortalAppsResponse, MiApp, AppDisponible, ModeloCobro, EstadoActivacion } from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'
import AgendaModulo, { type AgendaTab } from './apps/AgendaModulo'
import ClavesGateway from './apps/ClavesGateway'

type Tab = 'resumen' | AgendaTab | 'claves'
const TAB_LABEL: Record<Tab, string> = {
  resumen: 'Resumen',
  tipos: 'Tipos de reunión',
  disponibilidad: 'Disponibilidad',
  reservas: 'Reservas',
  link: 'Mi link',
  perfil: 'Perfil',
  apikeys: 'API keys',
  claves: 'Claves de API',
}
// "Tipos de reunión" va primero: copiar el enlace es lo que el organizador hace
// a diario; la información de la app es contexto y queda segunda.
const TABS_AGENDA: Tab[] = ['tipos', 'resumen', 'disponibilidad', 'reservas', 'link', 'perfil', 'apikeys']

// Apps API-only (sin UI propia): el cliente las consume desde sus sistemas, así
// que su módulo es la clave de API + el consumo.
const APPS_API_ONLY = new Set(['raglink', 'gemini-wrapper'])
const TABS_API: Tab[] = ['resumen', 'claves']

// El gateway reporta el consumo con SU nombre de servicio, que no siempre es el
// slug de la app (RAGLink mide como "docling"). Este mapa separa "el uso de esta
// app" del uso total de la cuenta, que es compartida por toda la plataforma.
const SERVICIO_DE_APP: Record<string, string> = {
  raglink: 'docling',
  'gemini-wrapper': 'gemini',
  agenda: 'agenda',
}

/** Bajo este saldo avisamos: gemini y docling cortan con 402 al llegar a cero. */
const SALDO_BAJO = 200

const ESTADO_BADGE: Record<EstadoActivacion, { cls: string; label: string }> = {
  activada: { cls: 'badge-activo', label: 'Activada' },
  pendiente: { cls: 'badge-pausado', label: 'Pendiente de canje' },
  revocada: { cls: 'badge-cancelado', label: 'Revocada' },
}

function fmtFecha(iso: string | null): string {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) } catch { return '' }
}

function inicial(nombre: string): string {
  return (nombre?.trim()?.[0] ?? '?').toUpperCase()
}

interface VistaApp {
  slug: string
  nombre: string
  descripcion: string | null
  descripcion_larga: string | null
  endpoint: string | null
  icono_url: string | null
  modelo_cobro: ModeloCobro | null
  etiquetas: string[]
  mi?: MiApp
}

interface UsoItem { fecha: string; servicio: string; dimension: string; cantidad: number; creditos: number }
interface ConsumoResp {
  disponible: boolean
  motivo?: string
  // `saldo_bajo`/`umbral_aviso` los decide el gateway (umbral configurable por
  // cuenta); el umbral local es solo respaldo si no vinieran.
  balance?: { account_id?: string; balance?: number; unidad?: string; umbral_aviso?: number; saldo_bajo?: boolean }
  usage?: { total_creditos?: number; items?: UsoItem[] }
}

export default function PortalAppDetalle() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { activeClienteId: clienteId } = usePortalCliente()
  const api = useApi()
  const [app, setApp] = useState<VistaApp | null>(null)
  const [loading, setLoading] = useState(true)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!clienteId || !slug) return
    ;(async () => {
      setLoading(true)
      try {
        const res = await api.get<PortalAppsResponse>(`/api/portal/apps?cliente_id=${clienteId}`)
        const mi = res?.mis_apps?.find(a => a.app_slug === slug)
        const disp = res?.disponibles?.find(a => a.slug === slug)
        if (mi) {
          setApp({
            slug: mi.app_slug,
            nombre: mi.nombre ?? mi.app_slug,
            descripcion: mi.descripcion,
            descripcion_larga: (disp as AppDisponible | undefined)?.descripcion_larga ?? null,
            endpoint: mi.endpoint ?? disp?.endpoint ?? null,
            icono_url: mi.icono_url ?? disp?.icono_url ?? null,
            modelo_cobro: mi.modelo_cobro ?? disp?.modelo_cobro ?? null,
            etiquetas: disp?.etiquetas ?? [],
            mi,
          })
        } else if (disp) {
          setApp({ ...disp, modelo_cobro: disp.modelo_cobro ?? null })
        } else {
          setApp(null)
        }
      } catch {
        setApp(null)
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, slug])

  // Consumo (saldo + uso) — solo si el cliente tiene la app activada. Degrada a
  // { disponible:false } mientras el gateway/mapping no estén listos.
  const [consumo, setConsumo] = useState<ConsumoResp | null>(null)
  useEffect(() => {
    if (!clienteId || !slug || !app?.mi) return
    api.get<ConsumoResp>(`/api/portal/apps/${slug}/consumo`)
      .then(r => setConsumo(r ?? { disponible: false }))
      .catch(() => setConsumo({ disponible: false }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, slug, app?.mi])

  // Módulo de Agenda: sub-pestañas + enlace público. La sesión se establece
  // server-side (SSO); acá solo guardamos la URL pública para "Mi link".
  // null = todavía no eligió; se resuelve según la app (Agenda abre en Tipos).
  const [tabElegida, setTabElegida] = useState<Tab | null>(null)
  const [publicUrl, setPublicUrl] = useState<string | null>(null)
  const [orgSlug, setOrgSlug] = useState<string | null>(null)
  const esAgendaActiva = app?.slug === 'agenda' && app?.mi?.estado === 'activada'
  const esApiActiva = !!app && APPS_API_ONLY.has(app.slug) && app.mi?.estado === 'activada'
  const tabsDelModulo: Tab[] | null = esAgendaActiva ? TABS_AGENDA : esApiActiva ? TABS_API : null
  const tab: Tab = tabElegida ?? (esAgendaActiva ? 'tipos' : 'resumen')
  const setTab = setTabElegida

  useEffect(() => {
    if (!esAgendaActiva) return
    api.post<{ public_url?: string | null; organizador_slug?: string | null }>('/api/portal/apps/agenda/session', {})
      .then(r => {
        setPublicUrl(r?.public_url ?? null)
        // El embed necesita el slug del organizador (no el de la app).
        setOrgSlug(r?.organizador_slug ?? r?.public_url?.split('/').filter(Boolean).pop() ?? null)
      })
      .catch(() => { setPublicUrl(null); setOrgSlug(null) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAgendaActiva])

  async function handleInteres() {
    if (!app) return
    setEnviando(true)
    try {
      await api.post('/api/portal/apps/interes', { app_slug: app.slug })
      showToast('¡Gracias! Te contactaremos para activar la app.', 'success')
    } catch {
      showToast('No se pudo registrar tu interés. Intentá de nuevo.', 'error')
    } finally {
      setEnviando(false)
    }
  }

  // Abrir Agenda: inicia sesión (SSO server-side) y abre la página del organizador.
  // Se abre la pestaña ANTES del await para no chocar con el bloqueador de pop-ups.
  const [abriendo, setAbriendo] = useState(false)
  async function handleAbrirAgenda() {
    if (!app) return
    setAbriendo(true)
    const w = window.open('', '_blank')
    try {
      const res = await api.post<{ public_url?: string | null }>('/api/portal/apps/agenda/session', {})
      const url = res?.public_url || app.endpoint || 'https://agenda.1conecta.com'
      if (w) w.location.href = url
      else window.location.href = url
    } catch {
      if (w) w.close()
      showToast('Tu Agenda estará disponible en breve.', 'error')
    } finally {
      setAbriendo(false)
    }
  }

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" />
        <p>Cargando app...</p>
      </div>
    )
  }

  if (!app) {
    return (
      <div className="pd">
        <button className="pad-back" onClick={() => navigate('/portal/apps')}>← Volver a Apps</button>
        <div className="pd-card"><p className="pa-desc">No encontramos esta app.</p></div>
      </div>
    )
  }

  const mc = app.modelo_cobro
  const mi = app.mi
  const badge = mi ? (ESTADO_BADGE[mi.estado] ?? ESTADO_BADGE.pendiente) : null

  return (
    <div className="pd pad">
      <Breadcrumbs
        items={[
          { label: 'Portal', to: '/portal' },
          { label: 'Apps', to: '/portal/apps' },
          { label: app.nombre },
          ...(tab !== 'resumen' ? [{ label: TAB_LABEL[tab] }] : []),
        ]}
      />

      {/* Hero */}
      <div className="pad-hero">
        {app.icono_url
          ? <img className="pad-logo pad-logo--img" src={app.icono_url} alt={app.nombre} />
          : <span className="pad-logo">{inicial(app.nombre)}</span>}
        <div className="pad-hero-info">
          <div className="pad-hero-titlerow">
            <span className="pad-hero-name">{app.nombre}</span>
            {badge && <span className={`badge ${badge.cls}`}>{badge.label}</span>}
          </div>
          {app.descripcion && <p className="pad-hero-sub">{app.descripcion}</p>}
          {app.etiquetas?.length > 0 && (
            <div className="pa-tags">{app.etiquetas.map(t => <span key={t} className="badge badge-categoria">{t}</span>)}</div>
          )}
        </div>
      </div>

      {/* Sub-navegación del módulo (solo apps con gestión propia) */}
      {tabsDelModulo && (
        <div className="ag-tabs">
          {tabsDelModulo.map(t => (
            <button
              key={t}
              type="button"
              className={`ag-tab ${tab === t ? 'ag-tab--active' : ''}`}
              onClick={() => setTab(t)}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
      )}

      {esAgendaActiva && tab !== 'resumen' ? (
        <AgendaModulo tab={tab as AgendaTab} publicUrl={publicUrl} orgSlug={orgSlug} />
      ) : esApiActiva && tab === 'claves' ? (
        <ClavesGateway endpoint={app.endpoint} />
      ) : (
      <div className="pad-grid">
        {/* Columna principal */}
        <div className="pad-main">
          <section className="pad-block">
            <h2 className="pad-block-title">Cómo funciona</h2>
            <p className="pad-prose">{app.descripcion_larga || app.descripcion || 'Pronto agregaremos más detalles de esta app.'}</p>
          </section>

          <section className="pad-block">
            <h2 className="pad-block-title">Cómo se consume el crédito</h2>
            {mc?.tarifas?.length ? (
              <div className="pad-tarifas-wrap">
                <table className="pad-tarifas">
                  <thead><tr><th>Modelo</th><th>Costo</th></tr></thead>
                  <tbody>
                    {mc.tarifas.map(t => (
                      <tr key={t.modelo}><td>{t.modelo}</td><td><strong>{t.creditos}</strong> créditos / {t.unidad}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : mc?.creditos ? (
              <p className="pad-prose"><strong>{mc.creditos} créditos</strong> por {mc.unidad || 'unidad'}.</p>
            ) : mc?.descripcion ? (
              <p className="pad-prose">{mc.descripcion}</p>
            ) : (
              <p className="pad-prose">El detalle de cobro se coordina al activar la app.</p>
            )}
            <p className="pad-note">
              Cada uso descuenta créditos de tu cuenta en la plataforma. Podés recargar y ver tu
              saldo en vivo desde el panel de la app; acá gestionás el acceso y los asientos.
            </p>
          </section>

          {mi && consumo && (
            <section className="pad-block">
              <h2 className="pad-block-title">Tu consumo</h2>
              {consumo.disponible ? (
                <>
                  {(() => {
                    const saldo = consumo.balance?.balance ?? 0
                    const servicio = SERVICIO_DE_APP[app.slug]
                    const items = consumo.usage?.items ?? []
                    const deEstaApp = servicio ? items.filter(i => i.servicio === servicio) : items
                    const totalApp = deEstaApp.reduce((s, i) => s + (i.creditos || 0), 0)
                    return (
                      <>
                        {(consumo.balance?.saldo_bajo ?? saldo < SALDO_BAJO) && (
                          <div className="pad-alerta">
                            Te quedan pocos créditos
                            {consumo.balance?.umbral_aviso != null && ` (aviso bajo ${consumo.balance.umbral_aviso})`}.
                            Cuando llegues a cero, las apps que cobran por uso dejan de responder.
                            Escribinos para recargar.
                          </div>
                        )}
                        <div className="pad-balance">
                          Saldo disponible: <strong>
                            {saldo.toLocaleString('es-PE', { maximumFractionDigits: 2 })}
                            {' '}{consumo.balance?.unidad || 'créditos'}
                          </strong>
                          <span className="pad-note" style={{ margin: '4px 0 0' }}>
                            Es el saldo de tu cuenta, compartido por todas tus apps.
                          </span>
                        </div>

                        {deEstaApp.length > 0 ? (
                          <>
                            <p className="pad-note" style={{ marginTop: 0 }}>
                              Uso de <strong>{app.nombre}</strong>: {totalApp.toLocaleString('es-PE', { maximumFractionDigits: 2 })} créditos
                            </p>
                            <div className="pad-tarifas-wrap">
                              <table className="pad-tarifas">
                                <thead><tr><th>Fecha</th><th>Uso</th><th>Créditos</th></tr></thead>
                                <tbody>
                                  {deEstaApp.map((it, i) => (
                                    <tr key={i}>
                                      <td>{it.fecha}</td>
                                      <td>{it.cantidad.toLocaleString('es-PE')} {it.dimension}</td>
                                      <td><strong>{it.creditos.toLocaleString('es-PE', { maximumFractionDigits: 2 })}</strong></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : (
                          <p className="pad-note">Todavía no registrás consumo de esta app.</p>
                        )}
                      </>
                    )
                  })()}
                </>
              ) : (
                <p className="pad-note">Tu consumo aparecerá acá cuando actives tu cuenta en la app.</p>
              )}
            </section>
          )}
        </div>

        {/* Panel lateral (acceso / CTA) */}
        <aside className="pad-side">
          <div className="pad-side-card">
            {mi ? (
              mi.estado === 'pendiente' ? (
                <>
                  <div className="pad-side-label">Tu código de activación</div>
                  <div className="pa-code">{mi.codigo}</div>
                  <p className="pa-code-help">
                    Canjealo en <a href={app.endpoint || 'https://raglink.1conecta.com'} target="_blank" rel="noreferrer">el panel de la app</a> para activar tu cuenta.
                    {mi.codigo_expira_at && <> Vence el {fmtFecha(mi.codigo_expira_at)}.</>}
                  </p>
                  <div className="pad-side-meta"><span><strong>{mi.asientos}</strong> {mi.asientos === 1 ? 'asiento' : 'asientos'}</span></div>
                </>
              ) : mi.estado === 'activada' ? (
                <>
                  <div className="pad-side-label">Acceso activo</div>
                  <div className="pad-side-meta"><span><strong>{mi.asientos}</strong> {mi.asientos === 1 ? 'asiento' : 'asientos'}</span></div>
                  {app.slug === 'agenda' ? (
                    <button className="btn btn-primary pad-cta" onClick={handleAbrirAgenda} disabled={abriendo}>
                      {abriendo ? 'Abriendo…' : 'Abrir mi Agenda'}
                    </button>
                  ) : (
                    <a href={app.endpoint || 'https://raglink.1conecta.com'} target="_blank" rel="noreferrer" className="btn btn-primary pad-cta">Abrir app</a>
                  )}
                </>
              ) : (
                <div className="pad-side-label">Esta activación fue revocada. Escribinos para reactivarla.</div>
              )
            ) : (
              <>
                <div className="pad-side-label">¿Te sirve esta app?</div>
                <p className="pad-side-text">Dejanos tu interés y coordinamos la activación para tu equipo.</p>
                <button className="btn btn-primary pad-cta" onClick={handleInteres} disabled={enviando}>
                  {enviando ? 'Enviando…' : 'Me interesa'}
                </button>
                <a href="https://wa.me/51986252983?text=Hola%2C%20quiero%20saber%20m%C3%A1s%20de%20una%20app" target="_blank" rel="noreferrer" className="btn btn-secondary pad-cta">Hablar con soporte</a>
              </>
            )}
          </div>
        </aside>
      </div>
      )}
    </div>
  )
}
